import { useEffect, useState } from 'react';
import { Save, Search, X } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';
import { FotoProduto } from '../../components/FotoProduto';
import { formatMoeda } from '@fiodecobre/shared';

interface Grupo {
  sigla: string;
  nome: string;
}

interface ProdutoOpcao {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  preco_pix: string;
  foto_url: string | null;
  estoque_atual: string;
  status?: string | null;
}

interface SlotProduto {
  produto_id: number | null;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  preco_pix: string;
  estoque_atual: string;
  busca: string;
  resultados: ProdutoOpcao[];
}

const slotVazio: SlotProduto = {
  produto_id: null, codigo_interno: null, descricao: '', foto_url: null,
  preco_pix: '0', estoque_atual: '0', busca: '', resultados: [],
};

interface Props {
  id: number | null;
  grupos: Grupo[];
  onFechar: () => void;
  onSalvo: () => void;
}

export function PromocaoFormModal({ id, grupos, onFechar, onSalvo }: Props) {
  const toast = useToast();
  const dialog = useDialog();
  const isEditing = id !== null;

  const [nome, setNome] = useState('');
  const [grupo, setGrupo] = useState('');
  const [ativo, setAtivo] = useState(true);
  const [slots, setSlots] = useState<SlotProduto[]>([
    { ...slotVazio }, { ...slotVazio }, { ...slotVazio },
  ]);
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (!isEditing) return;
    setCarregando(true);
    api.get<any>(`/api/promocoes/${id}`)
      .then((p) => {
        setNome(p.nome);
        setGrupo(p.grupo);
        setAtivo(p.ativo);
        const novos: SlotProduto[] = [{ ...slotVazio }, { ...slotVazio }, { ...slotVazio }];
        for (const prod of p.produtos || []) {
          const idx = prod.ordem - 1;
          if (idx >= 0 && idx < 3) {
            novos[idx] = {
              produto_id: prod.produto_id,
              codigo_interno: prod.codigo_interno,
              descricao: prod.descricao,
              foto_url: prod.foto_url,
              preco_pix: prod.preco_pix,
              estoque_atual: prod.estoque_atual,
              busca: '',
              resultados: [],
            };
          }
        }
        setSlots(novos);
      })
      .catch((err: any) => toast.erro(err?.message || 'Erro ao carregar'))
      .finally(() => setCarregando(false));
  }, [id, isEditing, toast]);

  async function buscarProdutos(idx: number, q: string) {
    setSlots((curr) => curr.map((s, i) => i === idx ? { ...s, busca: q } : s));
    if (q.trim().length < 2) {
      setSlots((curr) => curr.map((s, i) => i === idx ? { ...s, resultados: [] } : s));
      return;
    }
    try {
      const r = await api.get<ProdutoOpcao[]>(`/api/produtos?q=${encodeURIComponent(q)}&com_inativos=1`);
      const top = (r || []).slice(0, 15);
      setSlots((curr) => curr.map((s, i) => i === idx ? { ...s, resultados: top } : s));
    } catch {
      // silencia erro de busca
    }
  }

  function escolherProduto(idx: number, prod: ProdutoOpcao) {
    if (produtoInativo(prod)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
    setSlots((curr) => curr.map((s, i) => i === idx ? {
      produto_id: prod.id,
      codigo_interno: prod.codigo_interno,
      descricao: prod.descricao,
      foto_url: prod.foto_url,
      preco_pix: prod.preco_pix,
      estoque_atual: prod.estoque_atual,
      busca: '',
      resultados: [],
    } : s));
  }

  function limparSlot(idx: number) {
    setSlots((curr) => curr.map((s, i) => i === idx ? { ...slotVazio } : s));
  }

  async function salvar() {
    if (!nome.trim()) { toast.erro('Informe o nome'); return; }
    if (!grupo) { toast.erro('Selecione o grupo'); return; }
    if (slots.some((s) => !s.produto_id)) { toast.erro('Preencha os 3 produtos'); return; }
    const ids = slots.map((s) => s.produto_id);
    if (new Set(ids).size !== 3) { toast.erro('Os 3 produtos devem ser diferentes'); return; }

    setSalvando(true);
    try {
      const payload = {
        nome: nome.trim(),
        grupo,
        ativo,
        produtos: slots.map((s, i) => ({ produto_id: s.produto_id!, ordem: i + 1 })),
      };
      if (isEditing) {
        await api.put(`/api/promocoes/${id}`, payload);
      } else {
        await api.post('/api/promocoes', payload);
      }
      toast.sucesso(isEditing ? 'Promocao atualizada' : 'Promocao criada');
      onSalvo();
    } catch (err: any) {
      toast.erro(err?.message || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto={true}
      onFechar={onFechar}
      titulo={isEditing ? `Editar Promocao #${id}` : 'Nova Promocao'}
      largura={780}
      rodape={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onFechar} className="btn-secondary">Cancelar</button>
          <button onClick={salvar} disabled={salvando || carregando} className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Save size={14} /> {salvando ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      }
    >
      {carregando ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Carregando…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px auto', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Nome</label>
              <input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={100} placeholder="Ex: Trio Eletrica 1"
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Grupo</label>
              <select value={grupo} onChange={(e) => setGrupo(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white">
                <option value="">Selecione…</option>
                {grupos.map((g) => (
                  <option key={g.sigla} value={g.sigla}>{g.sigla} — {g.nome}</option>
                ))}
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, paddingBottom: 6, cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
              Ativa
            </label>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Produtos (exatamente 3)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {slots.map((slot, idx) => (
                <SlotRow key={idx} slot={slot} idx={idx}
                  onBuscar={buscarProdutos} onEscolher={escolherProduto} onLimpar={limparSlot} />
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

interface SlotRowProps {
  slot: SlotProduto;
  idx: number;
  onBuscar: (idx: number, q: string) => void;
  onEscolher: (idx: number, p: ProdutoOpcao) => void;
  onLimpar: (idx: number) => void;
}

function SlotRow({ slot, idx, onBuscar, onEscolher, onLimpar }: SlotRowProps) {
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, position: 'relative' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', minWidth: 22 }}>#{idx + 1}</div>
        {slot.produto_id ? (
          <>
            {slot.foto_url
              ? <FotoProduto url={slot.foto_url} tam={48} style={{ flexShrink: 0 }} />
              : <div style={{ width: 48, height: 48, background: '#f3f4f6', borderRadius: 4, flexShrink: 0 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
                <span style={{ color: '#6b7280', fontWeight: 700 }}>{slot.codigo_interno}</span>
                {' · '}
                {slot.descricao}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                Pix {formatMoeda(slot.preco_pix)} ·{' '}
                <span style={{ color: Number(slot.estoque_atual) > 0 ? '#15803d' : '#b91c1c' }}>
                  Estoque {Number(slot.estoque_atual).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}
                </span>
              </div>
            </div>
            <button onClick={() => onLimpar(idx)} className="btn-icon" title="Trocar produto">
              <X size={14} />
            </button>
          </>
        ) : (
          <div style={{ flex: 1, position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 8, top: 9, color: '#9ca3af' }} />
              <input value={slot.busca}
                onChange={(e) => onBuscar(idx, e.target.value)}
                placeholder="Busca produto (codigo, descricao, EAN)…"
                className="w-full pl-7 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            {slot.resultados.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                background: 'white', border: '1px solid #e5e7eb', borderRadius: 6,
                maxHeight: 260, overflowY: 'auto', zIndex: 50,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}>
                {slot.resultados.map((r) => (
                  <button key={r.id} onClick={() => onEscolher(idx, r)}
                    style={{
                      display: 'flex', gap: 8, alignItems: 'center', width: '100%',
                      padding: '6px 8px', textAlign: 'left', cursor: 'pointer',
                      background: 'transparent', border: 'none', borderBottom: '1px solid #f3f4f6',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {r.foto_url
                      ? <FotoProduto url={r.foto_url} tam={32} style={{ flexShrink: 0 }} />
                      : <div style={{ width: 32, height: 32, background: '#f3f4f6', borderRadius: 3, flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ color: '#6b7280', fontWeight: 700 }}>{r.codigo_interno}</span>
                        {' · '}{produtoInativo(r) && <SeloInativo />}{r.descricao}
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                        Pix {formatMoeda(r.preco_pix)} · Estoque {Number(r.estoque_atual).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
